import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiError } from '../../lib/utils';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Settings, Truck, Plus, Pencil, Trash2, Check, X, GripVertical } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', sla: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', sla: '', days: '' });

  const fetchDeliveryTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/delivery-types`);
      setDeliveryTypes(res.data.delivery_types);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryTypes();
  }, []);

  const handleAdd = async () => {
    if (!addForm.name || !addForm.sla) {
      toast.error('Please fill in both name and delivery time');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/delivery-types`, { ...addForm, days: parseInt(addForm.days) || 0 });
      toast.success('Delivery type added');
      setAddForm({ name: '', sla: '', days: '' });
      setShowAdd(false);
      fetchDeliveryTypes();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to add'));
    }
  };

  const handleEdit = async (code) => {
    if (!editForm.name || !editForm.sla) {
      toast.error('Please fill in both fields');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/admin/delivery-types/${code}`, { ...editForm, days: parseInt(editForm.days) || 0 });
      toast.success('Delivery type updated');
      setEditingCode(null);
      fetchDeliveryTypes();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update'));
    }
  };

  const handleDelete = async (code, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/delivery-types/${code}`);
      toast.success('Delivery type deleted');
      fetchDeliveryTypes();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to delete'));
    }
  };

  const handleToggle = async (code, currentActive) => {
    try {
      await axios.put(`${API_URL}/api/admin/delivery-types/${code}`, { active: !currentActive });
      toast.success(currentActive ? 'Deactivated' : 'Activated');
      fetchDeliveryTypes();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const startEdit = (dt) => {
    setEditingCode(dt.code);
    setEditForm({ name: dt.name, sla: dt.sla, days: dt.days ?? '' });
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-settings-page">
        <div className="flex items-center space-x-4 mb-8">
          <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <Settings className="h-6 w-6 text-slate-600" />
          </div>
          <div>
            <h1 className="page-title">Settings</h1>
            <p className="text-slate-600">Manage system configuration and delivery options.</p>
          </div>
        </div>

        {/* Delivery Types */}
        <Card className="card-elevated" data-testid="delivery-types-settings">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="card-title">Delivery Types</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">Manage delivery options and expected delivery times shown to customers.</p>
                </div>
              </div>
              {isSuperAdmin && (
                <Button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-delivery-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Type
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Add form */}
            {showAdd && (
              <div className="px-6 py-4 bg-teal-50/50 border-b border-slate-100 flex items-center gap-4" data-testid="add-delivery-type-form">
                <Input
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Type name (e.g. Express)"
                  className="flex-1 h-10"
                  data-testid="add-dt-name"
                />
                <Input
                  value={addForm.sla}
                  onChange={(e) => setAddForm({ ...addForm, sla: e.target.value })}
                  placeholder="Display text (e.g. 1 - 2 Business Days)"
                  className="flex-1 h-10"
                  data-testid="add-dt-sla"
                />
                <Input
                  type="number"
                  value={addForm.days}
                  onChange={(e) => setAddForm({ ...addForm, days: e.target.value })}
                  placeholder="Days"
                  className="w-24 h-10"
                  data-testid="add-dt-days"
                />
                <Button onClick={handleAdd} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-4">
                  <Check className="h-4 w-4 mr-1" /> Save
                </Button>
                <Button onClick={() => { setShowAdd(false); setAddForm({ name: '', sla: '', days: '' }); }} size="sm" variant="ghost" className="h-10">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Table */}
            <div className="divide-y divide-slate-100">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <div className="col-span-1">#</div>
                <div className="col-span-3">Delivery Type</div>
                <div className="col-span-3">Expected Delivery Time</div>
                <div className="col-span-1">Days</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>

              {deliveryTypes.map((dt, idx) => (
                <div key={dt.code} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50/50 transition-colors" data-testid={`dt-row-${dt.code}`}>
                  <div className="col-span-1 text-sm text-slate-400 font-medium">{idx + 1}</div>

                  {editingCode === dt.code ? (
                    <>
                      <div className="col-span-3">
                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-9" data-testid="edit-dt-name" />
                      </div>
                      <div className="col-span-3">
                        <Input value={editForm.sla} onChange={(e) => setEditForm({ ...editForm, sla: e.target.value })} className="h-9" data-testid="edit-dt-sla" />
                      </div>
                      <div className="col-span-1">
                        <Input type="number" value={editForm.days} onChange={(e) => setEditForm({ ...editForm, days: e.target.value })} className="h-9" data-testid="edit-dt-days" />
                      </div>
                      <div className="col-span-2"></div>
                      <div className="col-span-2 flex justify-end gap-2">
                        <Button size="sm" onClick={() => handleEdit(dt.code)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3">
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingCode(null)} className="h-8 px-3">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-span-3">
                        <p className="font-medium text-slate-900">{dt.name}</p>
                      </div>
                      <div className="col-span-3">
                        <p className="text-slate-600">{dt.sla}</p>
                      </div>
                      <div className="col-span-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-sm font-medium text-slate-700">
                          {dt.days != null && dt.days >= 0 ? dt.days : '—'}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <button
                          onClick={() => isSuperAdmin && handleToggle(dt.code, dt.active)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            dt.active
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                          data-testid={`dt-toggle-${dt.code}`}
                        >
                          {dt.active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                      <div className="col-span-2 flex justify-end gap-2">
                        {isSuperAdmin && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => startEdit(dt)} className="h-8 px-2 text-slate-500 hover:text-teal-700" data-testid={`dt-edit-${dt.code}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(dt.code, dt.name)} className="h-8 px-2 text-slate-500 hover:text-red-600" data-testid={`dt-delete-${dt.code}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}

              {deliveryTypes.length === 0 && !loading && (
                <div className="text-center py-12 text-slate-500">No delivery types configured</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
